import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {AlertTriangle, Plus, Play} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Progress, SegmentedProgress, type SegmentedProgressTone} from '@/components/ui/progress'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import type {ComboboxOption} from '@/components/ui/combobox'
import {PageLayout} from '@/components/shared/PageLayout'
import {useAuth} from '@/contexts/auth-context'
import {useAppConfig, DEFAULT_PAGINATION_CONFIG} from '@/hooks/use-app-config'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {formatDate} from '@/lib/date-utils'
import {CertificationCampaignStatus, CertificationCampaignStatusBadge} from '@/types/certification'
import type {CreateCampaignRequest} from '@/types/certification'
import {CertificationCampaignControllerCampaignsEndpoint} from '@/types/endpoint-contracts'
import {PageRequestFields} from '@/types/page'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {StackedBarLegend} from '@/components/shared/StackedBarLegend'
import {CampaignFormFields, isCampaignFormValid} from '@/features/certification/components/CampaignFormFields'

// Non-STARTED statuses for the campaign management widget — derived from the
// canonical enum so a new status added in the backend automatically shows up
// here unless it's STARTED (which has its own active-campaigns table above).
const NON_STARTED_STATUSES: ReadonlyArray<CertificationCampaignStatus> =
    Object.values(CertificationCampaignStatus).filter(
        (s) => s !== CertificationCampaignStatus.STARTED,
    )

type StackedBarSegmentTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

interface StackedBarSegment {
    value: number
    tone: StackedBarSegmentTone
    label: string
}

function StackedBar({segments}: {segments: StackedBarSegment[]}) {
    const total = segments.reduce((sum, s) => sum + s.value, 0)
    const progressSegments = total === 0 ? [] : segments
        .filter((s) => s.value > 0 && s.tone !== 'neutral')
        .map((s) => ({percent: (s.value / total) * 100, tone: s.tone as SegmentedProgressTone}))
    return (
        <Stack gap="sm">
            <SegmentedProgress segments={progressSegments}/>
            <StackedBarLegend items={segments.map((s) => ({label: s.label, value: s.value, tone: s.tone}))}/>
        </Stack>
    )
}

export function CertificationDashboard() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.certificationDashboard.pageTitle)
    const navigate = useNavigate()
    const {toast} = useToast()
    const {hasPermission, user} = useAuth()
    const {paginationConfig} = useAppConfig()
    const canCreate = hasPermission(permissions.certifications.create)

    const createDialog = useDialog()
    const generateDialog = useDialog()
    const [newCampaign, setNewCampaign] = useState<CreateCampaignRequest>({
        name: '',
        description: null,
        dueDate: '',
        ownerPartyId: user?.partyId ?? '',
    })
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('')

    // Local pagination state for the campaign-management widget. The dashboard
    // is a fixed-filter widget (status != STARTED) — no user-editable filters,
    // no URL coupling needed, so plain useState beats useServerPagination.
    const pageSizeOptions = paginationConfig?.pageSizeOptions ?? DEFAULT_PAGINATION_CONFIG.pageSizeOptions
    const defaultPageSize = paginationConfig?.defaultPageSize ?? DEFAULT_PAGINATION_CONFIG.defaultPageSize
    const [mgmtPage, setMgmtPage] = useState(0)
    const [mgmtPageSize, setMgmtPageSize] = useState(defaultPageSize)

    const {data: summary, isLoading: summaryLoading, error: summaryError} = useQuery({
        queryKey: ['certification-dashboard-summary'],
        queryFn: () => services.certification.dashboardSummary(),
        refetchInterval: 30000,
    })

    const {data: activeCampaigns = [], isLoading: activeCampaignsLoading, error: activeCampaignsError} = useQuery({
        queryKey: ['certification-dashboard-active-campaigns'],
        queryFn: () => services.certification.activeCampaigns(),
        refetchInterval: 30000,
    })

    // Server-side paged + filtered query: only non-STARTED campaigns. The CSV
    // status filter is wired through CertificationCampaignControllerCampaignsEndpoint
    // (kind=csv, element=enum), so the backend returns just the page we need —
    // no client-side .filter() or silent 1000-row truncation.
    //
    // Filter ↔ pagination interaction: the status filter is a constant set
    // derived from the enum at module scope (NON_STARTED_STATUSES). It cannot
    // change during the page lifetime — there is no UI to alter it — so the
    // only mutable inputs to pagination are mgmtPage / mgmtPageSize. There is
    // no "stale page after filter change" race to defend against. If a
    // status-filter UI is ever added, reset mgmtPage to 0 in its onChange.
    const mgmtQueryParams = useMemo(() => ({
        [CertificationCampaignControllerCampaignsEndpoint.filter.STATUS.name]: NON_STARTED_STATUSES.join(','),
        [PageRequestFields.page]: mgmtPage,
        [PageRequestFields.size]: mgmtPageSize,
    }), [mgmtPage, mgmtPageSize])

    const {data: nonStartedCampaignsPage, isLoading: allCampaignsLoading, error: allCampaignsError} = useQuery({
        queryKey: ['certification-campaigns', 'mgmt', mgmtPage, mgmtPageSize],
        queryFn: () => services.certification.listCampaigns(mgmtQueryParams),
    })

    const nonStartedCampaigns = nonStartedCampaignsPage?.content ?? []
    const mgmtTotalItems = nonStartedCampaignsPage?.totalElements ?? 0
    const mgmtTotalPages = nonStartedCampaignsPage?.totalPages ?? 0

    // Resolve selected owner identity for display
    const {data: selectedOwner} = useQuery({
        queryKey: ['identities', newCampaign.ownerPartyId],
        queryFn: () => services.identities.detail(newCampaign.ownerPartyId),
        enabled: !!newCampaign.ownerPartyId,
    })

    // Search identities for owner selector
    const {data: ownerSearchData, isLoading: ownerSearchLoading} = useQuery({
        queryKey: ['identities', 'search', ownerSearchQuery],
         
        queryFn: () => services.identities.list({q: ownerSearchQuery, size: 10}),
        
    })
    const ownerOptions: ComboboxOption[] = (ownerSearchData?.content || []).map((i) => ({
        value: i.id,
        label: i.party.displayName || i.identityType,
        description: i.identityType,
    }))

    const createMutation = useEntityMutation({
        mutationFn: (request: CreateCampaignRequest) =>
            services.certificationCampaigns.create(request),
        invalidate: ['certification-campaigns'],
        closeDialog: createDialog.close,
        onSuccess: (campaign) => {
            toast({description: i18n.certificationCampaigns.createSuccess({name: campaign.name})})
            navigate(routes.accessGovernance.certificationCampaigns.detail(campaign.id))
        },
    })

    const generateMutation = useEntityMutation({

        mutationFn: () => services.certification.generateCampaign({ownerPartyId: user?.partyId}),
        invalidate: ['certification-campaigns'],
        onSuccess: (result) => {
            const parts: string[] = []
            if (result.campaigns.length > 0) {
                parts.push(i18n.certificationCampaigns.generateSuccess({count: result.campaigns.length}))
            }
            if (result.skippedPolicies.length > 0) {
                parts.push(i18n.certificationCampaigns.generateSkipped({
                    count: result.skippedPolicies.length,
                    policies: result.skippedPolicies.join(', '),
                }))
            }
            if (parts.length === 0) {
                parts.push(i18n.certificationCampaigns.generateNoCampaigns)
            }
            toast({description: parts.join(' ')})
        },
    })

    const campaignStatusLabel = (status: CertificationCampaignStatus) => {
        switch (status) {
            case CertificationCampaignStatus.STAGING:
                return i18n.certificationCampaigns.statusStaging
            case CertificationCampaignStatus.BEFORE_GENERATION:
                return i18n.certificationCampaigns.statusBeforeGeneration
            case CertificationCampaignStatus.IN_GENERATION:
                return i18n.certificationCampaigns.statusInGeneration
            case CertificationCampaignStatus.STARTED:
                return i18n.certificationCampaigns.statusStarted
            case CertificationCampaignStatus.COMPLETED:
                return i18n.certificationCampaigns.statusCompleted
            case CertificationCampaignStatus.CANCELLED:
                return i18n.certificationCampaigns.statusCancelled
            default:
                return status
        }
    }

    const isLoading = summaryLoading || activeCampaignsLoading || allCampaignsLoading
    const error = summaryError || activeCampaignsError || allCampaignsError

    const pendingCount = (summary?.totalAssignments ?? 0) - (summary?.keepCount ?? 0) - (summary?.revokedCount ?? 0)

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.certificationDashboard}
            title={i18n.certificationDashboard.title}
            description={i18n.certificationDashboard.subtitle}
            loading={isLoading}
            error={error}
            data-testid="certification-dashboard"
        >

            {/* KPI Summary Row */}
            <DetailGrid columns={3} data-testid="certification-dashboard-summary">
                {/* Active Campaigns */}
                <Card data-testid="card-active-campaigns">
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.certificationDashboard.activeCampaigns}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <InlineRow className="items-baseline">
                            <StatValue size="lg" value={summary?.activeCampaigns ?? 0} data-testid="kpi-active-campaigns-value"/>
                            {(summary?.overdueCampaigns ?? 0) > 0 && (
                                <Badge variant="destructive" className="items-center gap-1" data-testid="kpi-overdue-badge">
                                    <AlertTriangle className="h-3 w-3"/>
                                    {summary?.overdueCampaigns} {i18n.certificationDashboard.overdue}
                                </Badge>
                            )}
                        </InlineRow>
                    </CardContent>
                </Card>

                {/* Review Progress */}
                <Card data-testid="card-review-progress">
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.certificationDashboard.reviewProgress}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <StatValue size="lg" className="mb-2" data-testid="kpi-review-progress-value" value={`${Math.round(summary?.completionPercent ?? 0)}%`}/>
                        <Progress value={summary?.completionPercent ?? 0} className="h-2 mb-1"/>
                        <MutedText size="xs" className="block">
                            {i18n.certificationDashboard.ofTotal({
                                completed: summary?.completedReviewItems ?? 0,
                                total: summary?.totalReviewItems ?? 0,
                            })}
                        </MutedText>
                    </CardContent>
                </Card>

                {/* Decisions Overview */}
                <Card data-testid="card-decisions-overview">
                    <CardHeader className="pb-2">
                        <CardDescription>{i18n.certificationDashboard.decisionsOverview}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <StatValue size="lg" className="mb-2" data-testid="kpi-decisions-overview-value" value={summary?.totalAssignments ?? 0}/>
                        <StackedBar segments={[
                            {value: summary?.keepCount ?? 0, tone: 'success', label: i18n.certificationDashboard.keep},
                            {value: summary?.revokedCount ?? 0, tone: 'danger', label: i18n.certificationDashboard.revoked},
                            {value: Math.max(0, pendingCount), tone: 'neutral', label: i18n.certificationDashboard.undecided},
                        ]}/>
                    </CardContent>
                </Card>

            </DetailGrid>

            {/* Active Campaigns Table */}
            <Card className="mt-6" data-testid="card-active-campaigns-table">
                <CardHeader>
                    <CardTitle data-testid="card-title-active-campaigns" className="text-lg">{i18n.certificationDashboard.activeCampaignsList}</CardTitle>
                    <CardDescription>{i18n.certificationDashboard.activeCampaignsListDesc}</CardDescription>
                </CardHeader>
                <CardContent>
                    {activeCampaigns.length === 0 ? (
                        <MutedText block centered className="py-8">{i18n.certificationDashboard.noCampaigns}</MutedText>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-campaign">{i18n.certificationDashboard.campaign}</TableHead>
                                    <TableHead data-testid="col-due-date">{i18n.certificationDashboard.dueDate}</TableHead>
                                    <TableHead data-testid="col-progress">{i18n.certificationDashboard.progress}</TableHead>
                                    <TableHead data-testid="col-keep">{i18n.certificationDashboard.keep}</TableHead>
                                    <TableHead data-testid="col-revoked">{i18n.certificationDashboard.revoked}</TableHead>
                                    <TableHead data-testid="col-undecided">{i18n.certificationDashboard.undecided}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {activeCampaigns.map((campaign) => (
                                    <TableRow key={campaign.id} data-testid={`dashboard-campaign-row-${campaign.id}`}>
                                        <TableCell className="font-medium">
                                            <TextLink
                                                to={routes.accessGovernance.certificationCampaigns.detail(campaign.id)}
                                                testId={`dashboard-campaign-link-${campaign.id}`}
                                            >
                                                <span data-testid={`dashboard-campaign-name-${campaign.id}`}>{campaign.name}</span>
                                            </TextLink>
                                        </TableCell>
                                        <TableCell>
                                            <InlineRow>
                                                {formatDate(campaign.dueDate)}
                                                {campaign.overdue && (
                                                    <Badge variant="destructive" className="text-xs">
                                                        {i18n.certificationDashboard.overdue}
                                                    </Badge>
                                                )}
                                            </InlineRow>
                                        </TableCell>
                                        <TableCell data-testid={`dashboard-campaign-progress-${campaign.id}`}>
                                            <InlineRow className="min-w-[140px]">
                                                <Progress value={campaign.completionPercent} className="h-2 flex-1"/>
                                                <MutedText size="sm" className="w-10 text-right">
                                                    {`${Math.round(campaign.completionPercent)}%`}
                                                </MutedText>
                                            </InlineRow>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={BadgeVariant.SUCCESS}>{campaign.keepCount}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="destructive">{campaign.revokedCount}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{campaign.undecidedCount}</Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Campaign Management — non-started campaigns */}
            <Card className="mt-6" data-testid="card-campaign-management">
                <CardHeader>
                    <InlineRow justify="between">
                        <div>
                            <CardTitle data-testid="card-title-campaign-management" className="text-lg">{i18n.certificationDashboard.campaignManagement}</CardTitle>
                            <CardDescription>{i18n.certificationDashboard.campaignManagementDesc}</CardDescription>
                        </div>
                        {canCreate && (
                            <InlineRow>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={generateDialog.open}
                                    disabled={generateMutation.isPending || !user?.partyId}
                                    data-testid="btn-generate-campaigns"
                                >
                                    <IconText icon={Play}>{i18n.certificationCampaigns.generate}</IconText>
                                </Button>
                                <Button size="sm" onClick={createDialog.open} data-testid="btn-create-campaign">
                                    <IconText icon={Plus}>{i18n.certificationCampaigns.createCampaign}</IconText>
                                </Button>
                            </InlineRow>
                        )}
                    </InlineRow>
                </CardHeader>
                <CardContent>
                    {nonStartedCampaigns.length === 0 ? (
                        <MutedText block centered className="py-8">{i18n.certificationDashboard.noManagementCampaigns}</MutedText>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-management-name">{i18n.certificationCampaigns.name}</TableHead>
                                        <TableHead data-testid="col-management-policy">{i18n.certificationCampaigns.policy}</TableHead>
                                        <TableHead data-testid="col-management-status">{i18n.certificationCampaigns.status}</TableHead>
                                        <TableHead data-testid="col-management-owner">{i18n.certificationCampaigns.owner}</TableHead>
                                        <TableHead data-testid="col-management-due-date">{i18n.certificationCampaigns.dueDate}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {nonStartedCampaigns.map((campaign) => (
                                        <ClickableTableRow
                                            key={campaign.id}
                                            to={routes.accessGovernance.certificationCampaigns.detail(campaign.id)}
                                            data-testid={`management-campaign-row-${campaign.id}`}
                                        >
                                            <TableCell className="font-medium">
                                                <TextLink
                                                    to={routes.accessGovernance.certificationCampaigns.detail(campaign.id)}
                                                    testId={`link-campaign-${campaign.id}`}
                                                    stopPropagation
                                                >
                                                    <span data-testid={`campaign-name-${campaign.id}`}>{campaign.name}</span>
                                                </TextLink>
                                                {campaign.description && (
                                                    <MutedText size="sm" className="block">
                                                        {campaign.description}
                                                    </MutedText>
                                                )}
                                            </TableCell>
                                            <TableCell data-testid={`campaign-policy-${campaign.id}`}>
                                                {campaign.policy ? (
                                                    <TextLink
                                                        to={routes.accessGovernance.certificationPolicies.detail(campaign.policy.id)}
                                                        testId={`campaign-policy-link-${campaign.id}`}
                                                        stopPropagation
                                                    >
                                                        {campaign.policy.displayName}
                                                    </TextLink>
                                                ) : '\u2014'}
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const campaignBadge = CertificationCampaignStatusBadge.of(campaign.status)
                                                    const CampaignIcon = campaignBadge?.icon
                                                    return (
                                                        <Badge variant={campaignBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1" data-testid={`campaign-status-${campaign.id}`}>
                                                            {CampaignIcon && <CampaignIcon className="h-3 w-3" aria-hidden="true"/>}
                                                            {campaignStatusLabel(campaign.status)}
                                                        </Badge>
                                                    )
                                                })()}
                                            </TableCell>
                                            <TableCell>{campaign.owners?.[0]?.ownerPartyName ?? '\u2014'}</TableCell>
                                            <TableCell>{formatDate(campaign.dueDate)}</TableCell>
                                        </ClickableTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Pagination
                                page={mgmtPage}
                                pageSize={mgmtPageSize}
                                totalItems={mgmtTotalItems}
                                totalPages={mgmtTotalPages}
                                onPageChange={setMgmtPage}
                                onPageSizeChange={(size) => {
                                    setMgmtPageSize(size)
                                    setMgmtPage(0)
                                }}
                                pageSizeOptions={pageSizeOptions}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Create Campaign Dialog */}
            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.createCampaign}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.createDescription}</DialogDescription>
                    </DialogHeader>
                    <CampaignFormFields
                        value={newCampaign}
                        onChange={setNewCampaign}
                        ownerOptions={ownerOptions}
                        onOwnerSearch={setOwnerSearchQuery}
                        ownerSearchLoading={ownerSearchLoading}
                        selectedOwnerLabel={selectedOwner?.party?.displayName || selectedOwner?.identityType}
                    />
                    <SaveCancelFooter
                        testIdPrefix="create"
                        onCancel={createDialog.close}
                        saving={createMutation.isPending}
                        disabled={!isCampaignFormValid(newCampaign)}
                        saveLabel={i18n.certificationCampaigns.createCampaign}
                        saveType="button"
                        onSave={() => createMutation.mutate(newCampaign)}
                    />
                </DialogContent>
            </Dialog>

            {/* Generate Campaigns Dialog */}
            <Dialog open={generateDialog.isOpen} onOpenChange={generateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.generate}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.generateDescription}</DialogDescription>
                    </DialogHeader>
                    <MutedText size="sm" className="py-2 block">
                        {i18n.certificationCampaigns.generateExplanation}
                    </MutedText>
                    <SaveCancelFooter
                        testIdPrefix="generate"
                        onCancel={generateDialog.close}
                        saving={generateMutation.isPending}
                        saveLabel={i18n.certificationCampaigns.generate}
                        saveType="button"
                        onSave={() => {
                            generateMutation.mutate()
                            generateDialog.close()
                        }}
                    />
                </DialogContent>
            </Dialog>

        </PageLayout>
    )
}
